import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

type UnsavedContextValue = {
  dirty: boolean;
  setDirty: (v: boolean) => void;
  confirmLeave: () => boolean;
};

const UnsavedContext = createContext<UnsavedContextValue | null>(null);

export function UnsavedProvider({ children }: { children: ReactNode }) {
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const confirmLeave = useCallback(() => {
    if (!dirty) return true;
    return window.confirm('You have unsaved changes. Leave without saving?');
  }, [dirty]);

  return (
    <UnsavedContext.Provider value={{ dirty, setDirty, confirmLeave }}>
      {children}
    </UnsavedContext.Provider>
  );
}

export function useUnsaved() {
  const ctx = useContext(UnsavedContext);
  if (!ctx) {
    return {
      dirty: false,
      setDirty: () => {},
      confirmLeave: () => true,
    };
  }
  return ctx;
}
